const ALLOWED_ROLES = ['administrador', 'vendedor'];
const ROLE_LABELS = {
  administrador: 'Administrador',
  vendedor: 'Vendedor',
};
const isAuthenticationPage = window.location.pathname.startsWith('/authentication/');

const roleState = {
  backendRole: undefined,  // undefined = not yet resolved; null = resolved but no role
  currentRole: null,
  pendingRole: null,
};

function getRoleLabel(role) {
  return ROLE_LABELS[role] || 'Sin rol';
}

function canUseAdminModeFromSession() {
  return sessionStorage.getItem('canUseAdminMode') === '1';
}

function hasAdminUiAccess() {
  if (canUseAdminModeFromSession()) return true;
  return sessionStorage.getItem('userRole') === 'administrador';
}

function getRoleRequestHeaders() {
  const headers = {
    Accept: 'application/json',
  };
  const jwtToken = localStorage.getItem('jwt_token');
  if (jwtToken) {
    headers.Authorization = `Bearer ${jwtToken}`;
  }
  return headers;
}

function isLocalOrDevRuntime() {
  const hosts = [window.location.hostname];
  try {
    hosts.push(new URL(API_BASE_URL, window.location.origin).hostname);
  } catch (_) {
    // Ignore malformed API_BASE_URL values
  }

  const joined = hosts.join(' ').toLowerCase();
  const isStagingOrProduction =
    joined.includes('app.demo.alexandria.com.es')
    || joined.includes('api.demo.alexandria.com.es')
    || joined.includes('app-stg.demo.alexandria.com.es')
    || joined.includes('api-stg.demo.alexandria.com.es');

  return !isStagingOrProduction;
}

/** Resolve role from backend auth payload and apply role-based visibility. */
async function resolveUserRole() {
  try {
    console.debug('[role] Resolving role from backend /api/me with fallback /api/auth/user', { API_BASE_URL });
    // window._alexandriaMePromise is started by me-bootstrap.js (or auth.js
    // if that script did not run) and resolves to the PARSED user object,
    // so this call shares the single /api/me/ request.
    let user = null;
    if (window._alexandriaMePromise instanceof Promise) {
      user = await window._alexandriaMePromise;
    } else {
      let response = await fetch(`${API_BASE_URL}/api/me/`, {
        method: 'GET',
        credentials: 'include',
        headers: getRoleRequestHeaders(),
      });
      if (response.status === 404) {
        response = await fetch(`${API_BASE_URL}/api/auth/user/`, {
          method: 'GET',
          credentials: 'include',
          headers: getRoleRequestHeaders(),
        });
      }
      if (!response.ok) {
        console.warn('[role] backend role endpoint failed', { status: response.status });
        const storedRole = sessionStorage.getItem('userRole');
        const fallbackRole = ALLOWED_ROLES.includes(storedRole) ? storedRole : 'vendedor';
        if (isLocalOrDevRuntime()) {
          return { backendRole: fallbackRole, effectiveRole: fallbackRole, user: null };
        }
        return { backendRole: null, effectiveRole: fallbackRole, user: null };
      }
      user = await response.json().catch(() => null);
    }

    if (!user) {
      console.warn('[role] /api/me/ returned no parseable user');
      const storedRole = sessionStorage.getItem('userRole');
      const fallbackRole = ALLOWED_ROLES.includes(storedRole) ? storedRole : 'vendedor';
      return { backendRole: null, effectiveRole: fallbackRole, user: null };
    }
    const backendRole = user?.role || null;
    console.debug('[role] Backend auth payload', {
      username: user?.username,
      role: backendRole,
      libreria_id: user?.libreria_id,
    });

    window.TenantContext?.setContextFromAuthUser(user, { source: 'user-role', silent: true });

    if (!backendRole) {
      console.warn('[role] Backend returned no role.');
      const storedRole = sessionStorage.getItem('userRole');
      const fallbackRole = ALLOWED_ROLES.includes(storedRole) ? storedRole : 'vendedor';
      if (isLocalOrDevRuntime()) {
        return { backendRole: fallbackRole, effectiveRole: fallbackRole, user };
      }
      return { backendRole: null, effectiveRole: fallbackRole, user };
    }

    let effectiveRole = backendRole;
    if (backendRole === 'administrador') {
      const storedRole = sessionStorage.getItem('userRole');
      if (storedRole && ALLOWED_ROLES.includes(storedRole)) {
        effectiveRole = storedRole;
        console.info('[role] Using admin-selected view role', { effectiveRole });
      } else {
        sessionStorage.setItem('userRole', backendRole);
      }
    } else {
      sessionStorage.setItem('userRole', backendRole);
    }

    console.info('[role] Applied role for UI', { backendRole, effectiveRole });
    return { backendRole, effectiveRole, user };
  } catch (error) {
    console.error('[role] Error resolving backend role', error);
    const storedRole = sessionStorage.getItem('userRole');
    const fallbackRole = ALLOWED_ROLES.includes(storedRole) ? storedRole : 'vendedor';
    if (isLocalOrDevRuntime()) {
      return { backendRole: fallbackRole, effectiveRole: fallbackRole, user: null };
    }
    return { backendRole: null, effectiveRole: fallbackRole, user: null };
  }
}

function updateAdministratorChip(userRole) {
  const chip = document.getElementById('administrator-role-chip');
  if (!chip) {
    return;
  }

  chip.classList.toggle('hidden', userRole !== 'administrador');
  chip.setAttribute('aria-hidden', userRole === 'administrador' ? 'false' : 'true');
}

function applyRoleVisibility(userRole) {
  if (ALLOWED_ROLES.includes(userRole)) {
    document.documentElement.setAttribute('data-ui-role', userRole);
  } else {
    document.documentElement.removeAttribute('data-ui-role');
  }

  updateAdministratorChip(userRole);
}

function setToggleChecked(role) {
  const roleToggle = document.getElementById('user-role-toggle');
  if (!roleToggle) {
    return;
  }

  roleToggle.checked = role === 'administrador';
}

function setToggleEnabled(enabled) {
  const roleToggle = document.getElementById('user-role-toggle');
  if (!roleToggle) {
    return;
  }

  const toggleLabel = roleToggle.closest('label');
  roleToggle.disabled = !enabled;

  if (toggleLabel) {
    toggleLabel.classList.toggle('opacity-50', !enabled);
    toggleLabel.classList.toggle('cursor-not-allowed', !enabled);
    toggleLabel.classList.toggle('cursor-pointer', enabled);
  }
}

function renderRoleControls() {
  const { backendRole, currentRole } = roleState;
  const roleToggle = document.getElementById('user-role-toggle');
  const currentBadge = document.getElementById('user-role-current-badge');
  const summaryTooltip = document.getElementById('user-role-summary-tooltip');
  const permissionNote = document.getElementById('user-role-permission-note');

  // Update data-account-role on <html> so the CSS rule in header.html can
  // hide #settings-menu-container for true vendedor accounts. This mirrors
  // what the early inline script does from sessionStorage, but now with the
  // confirmed backend role so the final state is always authoritative.
  // Admins browsing in vendedor view keep data-account-role="administrador".
  if (backendRole !== undefined) {
    document.documentElement.setAttribute(
      'data-account-role',
      backendRole === 'administrador' ? 'administrador' : 'vendedor'
    );
  }

  if (currentBadge) {
    currentBadge.textContent = getRoleLabel(currentRole);
    currentBadge.className = currentRole === 'administrador'
      ? 'inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-primary-700 transition hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-200 dark:hover:bg-primary-500/15'
      : 'inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10';
  }

  if (summaryTooltip) {
    if (currentRole === 'administrador') {
      summaryTooltip.textContent = 'Tienes activa la vista administrativa. Verás configuración avanzada, métricas y accesos restringidos.';
    } else {
      summaryTooltip.textContent = 'Tienes activa la vista operativa. La interfaz se simplifica para las tareas diarias de venta.';
    }
  }

  if (!roleToggle) {
    return;
  }

  setToggleChecked(currentRole);

  if (!backendRole) {
    const allowAdminToggle = hasAdminUiAccess();
    setToggleEnabled(allowAdminToggle);

    if (isLocalOrDevRuntime()) {
      roleToggle.removeAttribute('title');
      if (permissionNote) {
        permissionNote.textContent = '';
        permissionNote.classList.add('hidden');
      }
      return;
    }

    roleToggle.setAttribute('title', 'No se pudo verificar el rol desde backend.');
    if (permissionNote) {
      if (allowAdminToggle) {
        permissionNote.textContent = 'No se pudo verificar el rol desde backend ahora mismo. El cambio de vista sigue disponible con el contexto actual.';
        permissionNote.classList.remove('hidden');
      } else {
        permissionNote.textContent = '';
        permissionNote.classList.add('hidden');
      }
    }
    return;
  }

  if (backendRole !== 'administrador') {
    setToggleEnabled(false);
    roleToggle.setAttribute('title', 'Solo una cuenta administradora puede cambiar la vista.');
    if (permissionNote) {
      permissionNote.textContent = 'Tu cuenta opera como vendedor. Esta vista no permite alternar al modo administrador.';
      permissionNote.classList.remove('hidden');
    }
    return;
  }

  setToggleEnabled(true);
  roleToggle.removeAttribute('title');
  if (permissionNote) {
    permissionNote.classList.add('hidden');
    permissionNote.textContent = '';
  }
}

function closeSettingsDropdown() {
  const settingsDropdown = document.getElementById('settings-dropdown');
  const settingsButton = document.getElementById('settings-menu-button');

  if (settingsDropdown) {
    settingsDropdown.classList.add('hidden');
  }

  if (settingsButton) {
    settingsButton.setAttribute('aria-expanded', 'false');
  }
}

function openRoleConfirmation(nextRole) {
  const modal = document.getElementById('user-role-confirm-modal');
  const title = document.getElementById('user-role-confirm-title');
  const copy = document.getElementById('user-role-confirm-copy');
  const submit = document.getElementById('user-role-confirm-submit');

  if (!modal) {
    return;
  }

  roleState.pendingRole = nextRole;

  if (title) {
    title.textContent = nextRole === 'administrador'
      ? 'Cambiar a modo administrador'
      : 'Cambiar a modo vendedor';
  }

  if (copy) {
    copy.textContent = nextRole === 'administrador'
      ? 'Vas a activar la vista administrativa. La página se recargará para mostrar herramientas avanzadas y accesos de configuración.'
      : 'Vas a volver a la vista operativa. La página se recargará para simplificar la interfaz y ocultar herramientas administrativas.';
  }

  if (submit) {
    submit.textContent = nextRole === 'administrador' ? 'Activar administrador' : 'Cambiar a vendedor';
  }

  closeSettingsDropdown();
  modal.classList.remove('hidden');
  modal.classList.add('flex');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('overflow-hidden');
}

function closeRoleConfirmation({ revertToggle = true } = {}) {
  const modal = document.getElementById('user-role-confirm-modal');
  if (!modal) {
    return;
  }

  modal.classList.add('hidden');
  modal.classList.remove('flex');
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('overflow-hidden');

  if (revertToggle) {
    setToggleChecked(roleState.currentRole);
  }

  roleState.pendingRole = null;
}

function confirmRoleChange() {
  const nextRole = roleState.pendingRole;
  if (!ALLOWED_ROLES.includes(nextRole) || nextRole === roleState.currentRole) {
    closeRoleConfirmation();
    return;
  }

  sessionStorage.setItem('userRole', nextRole);
  closeRoleConfirmation({ revertToggle: false });

  if (nextRole === 'vendedor') {
    window.location.assign('/');
    return;
  }

  window.location.reload();
}

function bindRoleControls() {
  const roleToggle = document.getElementById('user-role-toggle');
  const modal = document.getElementById('user-role-confirm-modal');
  const closeButton = document.getElementById('user-role-confirm-close');
  const cancelButton = document.getElementById('user-role-confirm-cancel');
  const submitButton = document.getElementById('user-role-confirm-submit');

  if (roleToggle) {
    roleToggle.addEventListener('change', () => {
      if (roleToggle.disabled) {
        setToggleChecked(roleState.currentRole);
        return;
      }

      const nextRole = roleToggle.checked ? 'administrador' : 'vendedor';
      if (nextRole === roleState.currentRole) {
        return;
      }

      openRoleConfirmation(nextRole);
    });
  }

  if (closeButton) {
    closeButton.addEventListener('click', () => closeRoleConfirmation());
  }

  if (cancelButton) {
    cancelButton.addEventListener('click', () => closeRoleConfirmation());
  }

  if (submitButton) {
    submitButton.addEventListener('click', confirmRoleChange);
  }

  if (modal) {
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeRoleConfirmation();
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && roleState.pendingRole) {
      closeRoleConfirmation();
    }
  });
}

if (!isAuthenticationPage) {
  // Flag set to true once initRoleFromBackend has finished and dispatched
  // user-refreshed. The auth-context-ready listener checks this flag and
  // returns early on normal page loads to avoid a redundant /api/me/ call.
  var _authContextRoleApplied = false;

  bindRoleControls();

  const storedRole = sessionStorage.getItem('userRole');
  if (storedRole && ALLOWED_ROLES.includes(storedRole)) {
    roleState.currentRole = storedRole;
    applyRoleVisibility(storedRole);
    renderRoleControls();
  } else {
    updateAdministratorChip(null);
  }

  (async function initRoleFromBackend() {
    const hasJwtToken = Boolean(localStorage.getItem('jwt_token'));
    if (isLocalOrDevRuntime() && !hasJwtToken) {
      const fallbackRole = ALLOWED_ROLES.includes(sessionStorage.getItem('userRole'))
        ? sessionStorage.getItem('userRole')
        : 'vendedor';
      roleState.backendRole = fallbackRole;
      roleState.currentRole = fallbackRole;
      renderRoleControls();
      applyRoleVisibility(fallbackRole);
      return;
    }

    const { backendRole, effectiveRole, user } = await resolveUserRole();

    // ── Feature-flag session cache (ALEX-1321) ───────────────────────────────
    // Write after every successful /api/me/ resolution so the cache is fresh.
    // pf_features_lib records the current library_id for logout cleanup.
    if (user && user.libreria_id) {
      try {
        var _pfKey = 'pf_features_v1_' + String(user.libreria_id);
        sessionStorage.setItem(_pfKey, JSON.stringify({
          features: user.features || {},
          features_blocked: user.features_blocked || [],
          ts: Date.now(),
        }));
        sessionStorage.setItem('pf_features_lib', String(user.libreria_id));
      } catch (_) { /* sessionStorage unavailable (private browsing) */ }
    }

    console.debug('[role] Final resolved role for UI', { backendRole, effectiveRole });

    roleState.backendRole = backendRole;
    roleState.currentRole = ALLOWED_ROLES.includes(effectiveRole) ? effectiveRole : 'vendedor';
    renderRoleControls();
    applyRoleVisibility(roleState.currentRole);

    // Broadcast the user payload so navbar-user.js can apply the avatar.
    // This covers the normal page-load path. The auth-context-ready listener
    // below covers the role-switch reload path (where auth.js fires *after*
    // this IIFE has already resolved and set roleState.backendRole).
    if (user) {
      window.dispatchEvent(new CustomEvent('alexandria:user-refreshed', {
        detail: { user, source: 'user-role.js/init' },
      }));
    }

    // Inject feature-gated sidebar links (e.g. "Registro de actividad")
    // using the features dict from the /api/me/ response.
    //
    // Guard: call if sidebar.js has already run; otherwise stash the features
    // dict on window so sidebar.js can pick it up on its own initialisation
    // (script-order safety — whichever loads second drives the hydration).
    if (typeof window.hydrateSidebarFeatures === 'function') {
      window.hydrateSidebarFeatures(user?.features ?? null, backendRole, user?.features_blocked ?? []);
    } else {
      // sidebar.js hasn't executed yet — store features and role so it can
      // hydrate as soon as it runs (see window._pendingSidebarFeatures in sidebar.js).
      window._pendingSidebarFeatures = user?.features ?? null;
      window._pendingSidebarBackendRole = backendRole;
      window._pendingSidebarFeaturesBlocked = user?.features_blocked ?? [];
    }

    // Mark initRoleFromBackend as complete so the auth-context-ready listener
    // below can skip its resolveUserRole() call on normal page loads.
    _authContextRoleApplied = true;
  })();

  window.addEventListener('alexandria:auth-context-ready', async () => {
    // On a normal page load initRoleFromBackend completes first and sets
    // _authContextRoleApplied = true. In that case there is no need to
    // re-resolve — the user-refreshed event was already dispatched with the
    // authoritative data.
    if (_authContextRoleApplied) return;

    // On a normal page load auth-context-ready fires while initRoleFromBackend
    // is still in-flight (or has already resolved). Either way, initRoleFromBackend
    // will dispatch user-refreshed with the authoritative data. Re-fetching here
    // would be a redundant /api/me/ round-trip on every page load.
    //
    // We only need to re-resolve when auth-context-ready fires *after* the initial
    // role state is already set — which happens on a role-switch reload where
    // auth.js re-runs after the page reloads and roleState.backendRole is already
    // populated from the previous initRoleFromBackend run. In that case a fresh
    // fetch is needed to confirm the new role and re-apply the avatar.
    if (!roleState.backendRole) {
      // roleState.backendRole is still undefined (initRoleFromBackend hasn't
      // resolved yet) or null (no role returned, e.g. unlinked account).
      // Either way, initRoleFromBackend owns the dispatch for this load.
      return;
    }

    const { backendRole, effectiveRole, user } = await resolveUserRole();

    // ── Feature-flag session cache (ALEX-1321) ───────────────────────────────
    // Same cache-write on the role-switch reload path.
    if (user && user.libreria_id) {
      try {
        var _pfKey2 = 'pf_features_v1_' + String(user.libreria_id);
        sessionStorage.setItem(_pfKey2, JSON.stringify({
          features: user.features || {},
          features_blocked: user.features_blocked || [],
          ts: Date.now(),
        }));
        sessionStorage.setItem('pf_features_lib', String(user.libreria_id));
      } catch (_) { /* sessionStorage unavailable (private browsing) */ }
    }

    roleState.backendRole = backendRole;
    roleState.currentRole = ALLOWED_ROLES.includes(effectiveRole) ? effectiveRole : 'vendedor';
    renderRoleControls();
    applyRoleVisibility(roleState.currentRole);

    if (user) {
      window.dispatchEvent(new CustomEvent('alexandria:user-refreshed', {
        detail: { user, source: 'user-role.js/auth-ready' },
      }));
    }

    // Inject feature-gated sidebar links on the role-switch reload path.
    // hydrateSidebarFeatures is idempotent — if the placeholder was already
    // replaced by initRoleFromBackend, getElementById returns null and the
    // function returns silently.
    if (typeof window.hydrateSidebarFeatures === 'function') {
      window.hydrateSidebarFeatures(user?.features ?? null, backendRole, user?.features_blocked ?? []);
    } else {
      window._pendingSidebarFeatures = user?.features ?? null;
      window._pendingSidebarBackendRole = backendRole;
      window._pendingSidebarFeaturesBlocked = user?.features_blocked ?? [];
    }
  });
}
